import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EffectifByReasonMap } from '@shared/components/effective-needs/interfaces';
import { ScheduleResponse } from '@shared/models/campaignsInterfaces.model';
import { Observable, Subject, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { urlEndPoints } from '../config/end-points';
import { CalendarDal } from '../dal/calendar.dal';
import { Calendar, postCalendars } from '../models/calendar.model';
import { Profile } from '../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  public hoverCampaign: Subject<string> = new Subject<string>();

  public calendarValidated$ = new Subject<{ calendarId: string; isValidated: boolean }>();

  constructor(
    private httpClient: HttpClient,
    private calendarDal: CalendarDal
  ) {}

  emitCalendarValidation(calendarId: string, isValidated: boolean) {
    this.calendarValidated$.next({ calendarId, isValidated });
  }

  exportPlanningData(request: {
    startDate: string;
    endDate: string;
    hospitalId: string;
    populationId?: string;
    position: any[];
    reverseOrderName: boolean;
  }): Observable<ScheduleResponse> {
    let url =
      urlEndPoints.calendars +
      `exportPlanning?fromDate=${request.startDate}&toDate=${request.endDate}&position=${request.position}&reverseOrderName=${request.reverseOrderName}`;

    if (request.populationId) {
      url += `&populationId=${request.populationId}`;
    }

    return this.httpClient.post<ScheduleResponse>(url, null);
  }

  createDayAvailability(
    calendartoCreate: Calendar[],
    isExtraclinique: boolean,
    scenario: string | undefined = undefined,
    campaignId: string | undefined = undefined
  ): Observable<Calendar[]> {
    return this.httpClient.post<Calendar[]>(
      urlEndPoints.calendars + `?scenario=${scenario}&campaignId=${campaignId}`,
      postCalendars(calendartoCreate, isExtraclinique)
    );
  }

  changeDayAvailability(
    updatedCalendars: Calendar[],
    isExtraclinique: boolean,
    scenario: string | undefined = undefined,
    campaignId: string | undefined = undefined
  ): Observable<any> {
    return this.httpClient.put<Calendar[]>(
      urlEndPoints.calendars + `?scenario=${scenario}&campaignId=${campaignId}`,
      postCalendars(updatedCalendars, isExtraclinique)
    );
  }

  removeDayavailability(
    calendarId: string,
    scenario: string | undefined = undefined,
    campaignId: string | undefined = undefined
  ): Observable<any> {
    return this.httpClient.delete<any>(
      urlEndPoints.calendars + calendarId + `?scenario=${scenario}&campaignId=${campaignId}`
    );
  }

  deleteMultipleCalendars(profileId: string, fromDate: string, toDate: string): Observable<any> {
    return this.httpClient.delete<any>(
      urlEndPoints.calendars + `many?profileId=${profileId}&fromDate=${fromDate}&toDate=${toDate}`
    );
  }

  getAnesthesIsteGarde(day?: string): Observable<any[]> {
    return this.httpClient.get<Profile[]>(urlEndPoints.calendars + 'service/isgarde/Anesthésiste?day=' + day);
  }

  getInfirmiereAstreinte(day?: string): Observable<Profile[]> {
    return this.httpClient.get<Profile[]>(urlEndPoints.calendars + 'service/isgarde/Infirmiere?day=' + day);
  }

  updateTempCal(calendarId: string, isValidated: boolean, reasonMap?: any): Observable<any> {
    return this.httpClient.post<any>(urlEndPoints.calendars + 'updateTempCal', { calendarId, isValidated, reasonMap });
  }

  updatePeriodAvailability(
    calendars: Calendar[],
    fromDate: string,
    toDate: string,
    isExtraclinique: boolean
  ): Observable<any> {
    let params = new HttpParams();
    params = params.append('fromDate', fromDate);
    params = params.append('toDate', toDate);
    return this.httpClient.post<Calendar[]>(
      urlEndPoints.calendars + 'updateCal',
      postCalendars(calendars, isExtraclinique)[0],
      { params }
    );
  }

  requestCalendarsFromHospital(fromDate: string, toDate: string, hospitals: string[]) {
    return this.httpClient.post<any>(
      urlEndPoints.calendars + `requestCalendarsFromHospital?fromDate=${fromDate}&toDate=${toDate}`,
      {
        hospitals: hospitals,
      }
    );
  }

  relaunchAvailability(body: any) {
    return this.httpClient.post<any>(urlEndPoints.calendars + `relaunchAvailability`, body);
  }

  checkWorkTime(
    profileId: string,
    fromDate: string,
    toDate: string,
    startTime?: string,
    endTime?: string,
    hospitalId?: string
  ) {
    const request =
      urlEndPoints.calendars +
      'checkWorkTime' +
      '/' +
      profileId +
      '?fromDate=' +
      fromDate +
      '&toDate=' +
      toDate +
      (startTime ? '&startTime=' + startTime : '') +
      (endTime ? '&endTime=' + endTime : '') +
      (hospitalId ? '&hospital=' + hospitalId : '');
    return this.httpClient.get<any>(request);
  }

  getWorkTime(
    profileId: string,
    fromDate: string,
    toDate: string,
    startTime?: string,
    endTime?: string,
    hospitalIds?: any[]
  ) {
    const request =
      urlEndPoints.calendars +
      'getWorkTime' +
      '/' +
      profileId +
      '?fromDate=' +
      fromDate +
      '&toDate=' +
      toDate +
      (startTime ? '&startTime=' + startTime : '') +
      (endTime ? '&endTime=' + endTime : '');
    return this.httpClient.post<any>(request, hospitalIds);
  }

  getLastUpdateDate(startDate: string, endDate: string, positions: string[]): Observable<any> {
    return timer(2000).pipe(
      switchMap(() =>
        this.httpClient.get<any>(
          urlEndPoints.calendars +
            'lastUpdateDate?fromDate=' +
            startDate +
            '&toDate=' +
            endDate +
            '&positions=' +
            positions.join(',')
        )
      )
    );
  }

  countEffectifByReasonMap(
    fromDate: string,
    toDate: string,
    positions: string[],
    period?: string,
    scenarioId?: string,
    campaignId?: string,
    profilesIds?: string[],
    residency?: string
  ): Observable<EffectifByReasonMap[]> {
    return this.httpClient.get<EffectifByReasonMap[]>(
      urlEndPoints.calendars +
        'countEffectifByReasonMap?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&positions=' +
        positions.join(',') +
        (period ? '&period=' + period : '') +
        (scenarioId ? '&scenario=' + scenarioId : '') +
        (campaignId ? '&campaign=' + campaignId : '') +
        (profilesIds ? '&profiles=' + profilesIds.join(',') : '') +
        (residency ? '&residency=' + residency : '')
    );
  }

  getProfilesCalendarsByReasonMaps(
    fromDate: string,
    toDate: string,
    reasonMapsIds: string[],
    page: number,
    limit: number,
    search?: string,
    residency?: string,
    availability?: string,
    date?: string,
    isNight?: boolean
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.calendars +
        'getProfilesCalendarsByReasonMaps?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&reasonMapsIds=' +
        reasonMapsIds.join(',') +
        '&page=' +
        page +
        '&limit=' +
        limit +
        (search ? '&search=' + search : '') +
        (residency ? '&residency=' + residency : '') +
        (availability ? '&availability=' + availability : '') +
        (date ? '&date=' + date : '') +
        (isNight ? '&isNight=' + isNight : '')
    );
  }

  getUnmetData(request: {
    startDate: string;
    endDate: string;
    hospitalId: string;
    populationId?: string;
    positions: any[];
  }): Observable<any> {
    let url =
      urlEndPoints.calendars +
      `unmetNeeds?fromDate=${request.startDate}&toDate=${request.endDate}&formationsGroups=${request.positions.join(',')}&hospital=${request.hospitalId}`;

    if (request.populationId) {
      url += `&populationId=${request.populationId}`;
    }

    return this.httpClient.get<any>(url);
  }

  clear() {
    this.hoverCampaign.complete();
    this.hoverCampaign = new Subject<string>();
  }

  getDayCoverageEtp(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean,
    isBreakTimeIncludedInStats: boolean
  ): Observable<any> {
    return this.calendarDal.getDayCoverageEtp(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume,
      isBreakTimeIncludedInStats
    );
  }

  getNightCoverageEtp(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean
  ): Observable<any> {
    return this.calendarDal.getNightCoverageEtp(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume
    );
  }

  getTempStaffCount(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean
  ): Observable<any> {
    return this.calendarDal.getTempStaffCount(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume
    );
  }

  getDayWorkloadRate(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean
  ): Observable<any> {
    return this.calendarDal.getDayWorkloadRate(fromDate, toDate, populationId, formations, includeWeekends);
  }

  getAbsenceCount(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean
  ): Observable<any> {
    return this.calendarDal.getAbsenceCount(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume
    );
  }

  getNightWorkloadRate(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean
  ): Observable<any> {
    return this.calendarDal.getNightWorkloadRate(fromDate, toDate, populationId, formations, includeWeekends);
  }

  getCoverageEtpByWeekDays(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean,
    isBreakTimeIncludedInStats: boolean
  ): Observable<any> {
    return this.calendarDal.getCoverageEtpByWeekDays(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume,
      isBreakTimeIncludedInStats
    );
  }

  getCoverageEtpBySpecialties(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean,
    isBreakTimeIncludedInStats: boolean
  ): Observable<any> {
    return this.calendarDal.getCoverageEtpBySpecialties(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume,
      isBreakTimeIncludedInStats
    );
  }

  getCodesCountByProfiles(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    campaignId?: string,
    scenarioId?: string
  ): Observable<any> {
    return this.calendarDal.getCodesCountByProfiles(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      campaignId,
      scenarioId
    );
  }

  getSkillsAnalyticsBySpecialties(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean,
    isBreakTimeIncludedInStats: boolean
  ): Observable<any> {
    return this.calendarDal.getSkillsAnalyticsBySpecialties(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume,
      isBreakTimeIncludedInStats
    );
  }

  getMeanDaysToLevel3(
    fromDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean
  ): Observable<any> {
    return this.calendarDal.getMeanDaysToLevel3(fromDate, populationId, formations, includeWeekends);
  }

  getCoverageEtpBySurgeons(
    fromDate: string,
    toDate: string,
    populationId: string | undefined,
    formations: string | undefined,
    includeWeekends: boolean,
    showHourlyVolume: boolean,
    isBreakTimeIncludedInStats: boolean
  ): Observable<any> {
    return this.calendarDal.getCoverageEtpBySurgeons(
      fromDate,
      toDate,
      populationId,
      formations,
      includeWeekends,
      showHourlyVolume,
      isBreakTimeIncludedInStats
    );
  }
}
